import { assert } from "@faro-lotv/foundation";
import { ReadLsDataV2Response, LsRootObject, LsScan } from "@src/api/stagingarea-api/stagingarea-api-types";
import { getEntitiesPath } from "@utils/capture-tree/capture-tree-utils";
import { FileExt } from "@utils/file-ext";

const KIBIBYTE = 1024;
const MEBIBYTE = KIBIBYTE * KIBIBYTE;

/**
 * Binary approach to extract the Orbis project and dataset UUIDs form the file.
 *
 * The .geoslam file is a SQLite 3 binary file. Unfortunately, sql.js loads the entire file into memory.
 * We can't have that here as the files are several GB in size.
 *
 * @param file Orbis file (.geoslam)
 * @param chunkSize Defaults to 1 MiB.
 * @returns The project and dataset UUIDs.
 * @throws If the UUIDs are found in the file.
 */
export async function extractUUIDsFromFile(
  file: File,
  chunkSize = MEBIBYTE
): Promise<{ projectId: string, datasetId: string }> {

  /**
   * Read the file of type SQLite3 as binary and search for the project and dataset UUIDs.
   *
   * Using SQLite3 would be nicer, but it has to load the entire file into memory.
   * This is not feasible for large files (several GB). In future, we might not need this
   * function anymore, as the metadata should be part of LsDataV2.
   *
   * We read the file in chunks of `chunkSize` bytes, decode them to text,
   * search for the expected prefix and postfix in the text and extract the UUIDs.
   * We expect to find the string within the first chunk, but we keep reading, if it is not found.
   *
   * Example:
   * original_filename/data/projects/39810882-8da4-4381-be58-0054e02b694d/datasets/
   * 4e1d12bc-6b89-4a8b-8141-0b8f6398f64f/mobile/results/4e1d12bc-6b89-4a8b-8141-0b8f6398f64f.geoslam
   *
   * projectId: 39810882-8da4-4381-be58-0054e02b694d
   * datasetId: 4e1d12bc-6b89-4a8b-8141-0b8f6398f64f
   */

  const uuidRegex = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
  const pathPrefix = "original_filename/data/projects/";
  const pathPostfix = ".geoslam";
  const decoder = new TextDecoder();

  const foundUUIDs: string[] = [];

  // Number of 1 byte characters to overlap between chunks.
  // 174 is the expected length, so 200 is a save overlap.
  const overlap = 200;

  let offset = 0;
  let leftover = "";
  while (offset < file.size) {
    const end = Math.min(offset + chunkSize, file.size);
    const blob = file.slice(offset, end);
    const buffer = await blob.arrayBuffer();
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const text = decoder.decode(buffer, { stream: true });

    // Combine leftover from previous chunk (to catch split UUIDs/paths).
    const combined = leftover + text;

    const pathStart = combined.indexOf(pathPrefix);
    if (pathStart > -1) {
      const pathEnd = combined.indexOf(pathPostfix, pathStart);
      if (pathEnd > -1) {
        // Find matches using the UUID regex.
        const matches = combined.slice(pathStart, pathEnd).match(uuidRegex);
        if (matches) {
          matches.forEach((uuid) => foundUUIDs.push(uuid));
          break;
        }
      }
    }

    leftover = combined.slice(chunkSize - overlap);
    offset += chunkSize;
  }

  assert(foundUUIDs.length >= 2, "Not enough UUIDs found in the file");

  return {
    projectId: foundUUIDs[0],
    datasetId: foundUUIDs[1],
  };
}

/**
 * Creates a dummy LsDataV2 object for single orbis files
 */
export function createOrbisDummyLsDataV2(orbisFile: File | FileExt, datasetId: string): ReadLsDataV2Response {
  // Minimal root object.
  const rootObject: LsRootObject = {
    name: "RootObject",
    uuid: "00000000-0000-0000-0000-000000000000",
    revision: "0",
    treeLevel: 0,
    treePath: "root",
    ancestorUuids: [],
  };

  // Create LsScan object for the Orbis file.
  const scan: LsScan = {
    uuid: datasetId,
    // We need the correct root uuid here, as the Orbis upload uses the same path as the Blink upload
    parentUuid: rootObject.uuid,
    name: orbisFile.name,
    description: "",
    recordingTime: orbisFile.lastModified ? new Date(orbisFile.lastModified).toISOString() : undefined,
    files: [
      {
        path: orbisFile.name,
      },
    ],
    treeLevel: 1,
    treePath: getEntitiesPath([rootObject.name, orbisFile.name]),
    ancestorUuids: [rootObject.uuid],
  };

  // Map from scan filename (lowercase) to scan.
  const scansByFilename: Record<string, LsScan> = {};
  scansByFilename[scan.name.toLowerCase()] = scan;

  // Map from scan UUID to scan.
  const scansByUuid: Record<string, LsScan> = {};
  scansByUuid[scan.uuid] = scan;

  return {
    rootObject,
    projects: [],
    clusters: [],
    scans: [scan],
    edges: [],
    maxTreeLevel: 1,
    treeObjectsByUuid: {
      ...scansByUuid,
    },
    clustersByUuid: {},
    scansByUuid,
    scansByFilename,
  };
}
